import { isSupportedNativeSdkProvider } from '../hooks/digital-wallets/native-sdk';

// PRX-24667: the return addresses of a native-SDK method (Revolut Pay
// `mobileRedirectUrls`) and their recognition at boot.

export const NATIVE_SDK_RETURN_PARAMS = {
  Provider: 'native_sdk',
  Gateway: 'native_sdk_gateway',
  Result: 'native_sdk_result',
} as const;

export const NATIVE_SDK_RETURN_OUTCOMES = {
  Success: 'success',
  Failure: 'failure',
  Cancel: 'cancel',
} as const;

// Revolut adds the order token to the return address when an order exists.
export const REVOLUT_ORDER_PARAM = '_rp_oid';

type TOutcome = (typeof NATIVE_SDK_RETURN_OUTCOMES)[keyof typeof NATIVE_SDK_RETURN_OUTCOMES];

/**
 * Adds the provider, gateway and outcome parameters to a Cashier address,
 * in the query part before any hash (the address builder adds the hash last).
 */
export const addNativeSdkReturnParams = (
  url: string,
  {
    provider,
    gatewayId,
    outcome,
  }: { provider: string; gatewayId: number | string; outcome: TOutcome },
): string => {
  const hashIndex = url.indexOf('#');
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const params = new URLSearchParams({
    [NATIVE_SDK_RETURN_PARAMS.Provider]: provider,
    [NATIVE_SDK_RETURN_PARAMS.Gateway]: String(gatewayId),
    [NATIVE_SDK_RETURN_PARAMS.Result]: outcome,
  });
  const separator = !base.includes('?') ? '?' : base.endsWith('?') || base.endsWith('&') ? '' : '&';

  return `${base}${separator}${params.toString()}${hash}`;
};

/**
 * Every outcome leads to the existing `callback` return (the result page of
 * the session transaction): the SDK redirects only after `createOrder`
 * returned the order, so every return belongs to a transaction.
 * `getRedirectUrl` is the return-address builder of withRedirectWindow.
 */
export const buildNativeSdkMobileRedirectUrls = (
  getRedirectUrl: (callback?: boolean) => string,
  { provider, gatewayId }: { provider: string; gatewayId: number | string },
) => ({
  success: addNativeSdkReturnParams(getRedirectUrl(true), {
    provider,
    gatewayId,
    outcome: NATIVE_SDK_RETURN_OUTCOMES.Success,
  }),
  failure: addNativeSdkReturnParams(getRedirectUrl(true), {
    provider,
    gatewayId,
    outcome: NATIVE_SDK_RETURN_OUTCOMES.Failure,
  }),
  cancel: addNativeSdkReturnParams(getRedirectUrl(true), {
    provider,
    gatewayId,
    outcome: NATIVE_SDK_RETURN_OUTCOMES.Cancel,
  }),
});

/**
 * PRX-24950: the deep link of the merchant app (`app_return_url` of the
 * session) for the SDK `appReturnUrl`. Only in a WebView of an app (the
 * user-agent detection); the format is checked when the session is created.
 * Nothing otherwise, and the method stays available.
 */
export const getNativeSdkAppReturnUrl = (
  appReturnUrl: unknown,
  isInAppBrowser: boolean,
): string | null =>
  isInAppBrowser && typeof appReturnUrl === 'string' && appReturnUrl.trim() ? appReturnUrl : null;

/** The return of a native-SDK method, whatever the outcome. */
export const isNativeSdkReturn = (params: URLSearchParams): boolean =>
  isSupportedNativeSdkProvider(params.get(NATIVE_SDK_RETURN_PARAMS.Provider));
